import type { CameraStatus, Severity } from '@playplate/shared';

/**
 * Formatting helpers.
 *
 * Everything a human reads is rendered in the browser's local timezone, which
 * for a store dashboard is the store's timezone. ISO strings are kept for
 * machine use and shown only in tooltips.
 */

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return timeFormatter.format(new Date(iso));
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dateTimeFormatter.format(new Date(iso));
}

/**
 * Relative time, short form. "3m ago" beats a timestamp on a live dashboard —
 * the question is always "how stale is this", not "what o'clock was it".
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 0) return 'just now';
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${Math.floor(seconds)}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  const hours = Math.floor(seconds / 3600);
  return `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat().format(value);
}

/** Hour of day as a readable band, e.g. 19 -> "19:00–20:00". */
export function formatHourBand(hour: number | null): string {
  if (hour === null) return '—';
  return `${String(hour).padStart(2, '0')}:00–${String((hour + 1) % 24).padStart(2, '0')}:00`;
}

export function humanEventType(type: string): string {
  return type
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const SEVERITY_STYLES: Record<Severity, { text: string; bg: string; border: string; dot: string }> = {
  CRITICAL: { text: 'text-critical', bg: 'bg-critical/10', border: 'border-critical/40', dot: 'bg-critical' },
  HIGH: { text: 'text-high', bg: 'bg-high/10', border: 'border-high/40', dot: 'bg-high' },
  MEDIUM: { text: 'text-medium', bg: 'bg-medium/10', border: 'border-medium/40', dot: 'bg-medium' },
  LOW: { text: 'text-low', bg: 'bg-low/10', border: 'border-low/40', dot: 'bg-low' },
};

export const CAMERA_STATUS_STYLES: Record<CameraStatus, { label: string; text: string; dot: string }> = {
  ONLINE: { label: 'Online', text: 'text-online', dot: 'bg-online' },
  // Degraded is deliberately amber rather than green: a camera delivering two
  // frames a second is not fine, and a binary up/down display hides exactly the
  // failure that is hardest to notice.
  DEGRADED: { label: 'Degraded', text: 'text-high', dot: 'bg-high' },
  OFFLINE: { label: 'Offline', text: 'text-critical', dot: 'bg-critical' },
  ERROR: { label: 'Error', text: 'text-critical', dot: 'bg-critical' },
  CONNECTING: { label: 'Connecting', text: 'text-muted', dot: 'bg-muted' },
  DISABLED: { label: 'Disabled', text: 'text-muted/60', dot: 'bg-muted/40' },
};
